import { useState } from 'react'
import {
    Typography,
    Button,
    TextField,
    Divider,
    IconButton,
    InputAdornment,
} from '@mui/material'
import {
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material'
import { Grid } from '../../UniversalComponents'
import { addUser } from '../../../apiRoutes/userRoutes'

interface ConfirmRegistrationFormProps {
    // UserRegistration id from the /verify-user/<id> email link.
    registrationId: string
    // The staged UserRegistration (from getRegistrationById): user + boat +
    // manufacturer. Every field on this form is prefilled from it.
    registration: any
    // Called after the account is successfully created.
    onCreated: () => void
}

// Confirm Registration Form — opened from the registration email link. The
// invited user's details already live on the UserRegistration, so this form
// prefills them, has the user confirm/edit and set a password, and creates
// their account. The server assigns the Customer profile and auto-favorites
// the boat they were registered to (see /auth-create-user).
const ConfirmRegistrationForm = ({
    registrationId,
    registration,
    onCreated,
}: ConfirmRegistrationFormProps) => {
    const [error, setError] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    // The form's starting values, prefilled from the UserRegistration passed in
    // by the verify-user route. Kept around so Reset can drop everything the
    // user typed and put the prefilled details back exactly as they arrived.
    const [initialFormData] = useState(() => ({
        userEmail: registration?.userEmail || '',
        password: '',
        confirmPassword: '',
        userFirstName: registration?.userFirstName || '',
        userLastName: registration?.userLastName || '',
        userPhoneNumber: registration?.userPhoneNumber || '',
        userAddress: registration?.userAddress || '',
    }))

    const [formData, setFormData] = useState(initialFormData)

    // Read-only boat context. The boat is auto-favorited server-side on create.
    const model = registration?.boat?.boatModel
    const boatInfo = {
        fullHin: registration?.boat?.fullHin || '',
        boatModel: model ? `${model.modelYear} - ${model.model}` : '',
        manufacturer: registration?.manufacturer?.manufacturerName || '',
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    // Clears everything the user entered — passwords and any edits — leaving
    // only the details that were prefilled from the registration.
    const handleReset = () => {
        setFormData(initialFormData)
        setShowPassword(false)
        setShowConfirmPassword(false)
        setError('')
    }

    const handleConfirm = async () => {
        setError('')
        const {
            password,
            confirmPassword,
            userFirstName,
            userLastName,
            userPhoneNumber,
            userAddress,
        } = formData

        if (!userFirstName || !userLastName) {
            setError('Please enter your first and last name.')
            return
        }
        if (!password || password.length < 6) {
            setError('Password must be at least 6 characters.')
            return
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.')
            return
        }
        try {
            setSubmitting(true)
            const res: any = await addUser({
                _id: registrationId,
                password,
                userFirstName,
                userLastName,
                userPhoneNumber,
                userAddress,
            })
            if (res && res.data) {
                onCreated()
            } else {
                setError('Account could not be created. Please try again.')
            }
        } catch (err: any) {
            setError('Account could not be created. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography
                    variant="h5"
                    color={'primary'}
                    align="center"
                    sx={{ marginBottom: 1 }}
                >
                    Confirm Registration Form
                </Typography>
                <Typography color={'primary'} align="center">
                    Confirm your details and set a password to create your
                    account.
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle1" color={'primary'}>
                    User Information
                </Typography>
                <Divider sx={{ marginBottom: 1 }} />
            </Grid>

            <Grid item xs={12}>
                <TextField
                    label="E-Mail"
                    name="userEmail"
                    fullWidth
                    value={formData.userEmail}
                    disabled
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    name="password"
                    fullWidth
                    required
                    value={formData.password}
                    onChange={handleChange}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showPassword
                                                ? 'Hide password'
                                                : 'Show password'
                                        }
                                        onClick={() =>
                                            setShowPassword((p) => !p)
                                        }
                                        edge="end"
                                    >
                                        {showPassword ? (
                                            <VisibilityOffIcon />
                                        ) : (
                                            <VisibilityIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    name="confirmPassword"
                    fullWidth
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showConfirmPassword
                                                ? 'Hide confirm password'
                                                : 'Show confirm password'
                                        }
                                        onClick={() =>
                                            setShowConfirmPassword((p) => !p)
                                        }
                                        edge="end"
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityOffIcon />
                                        ) : (
                                            <VisibilityIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    label="First Name"
                    name="userFirstName"
                    fullWidth
                    required
                    value={formData.userFirstName}
                    onChange={handleChange}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    label="Last Name"
                    name="userLastName"
                    fullWidth
                    required
                    value={formData.userLastName}
                    onChange={handleChange}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    label="Phone Number (Optional)"
                    name="userPhoneNumber"
                    fullWidth
                    value={formData.userPhoneNumber}
                    onChange={handleChange}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    label="Address (Optional)"
                    name="userAddress"
                    fullWidth
                    value={formData.userAddress}
                    onChange={handleChange}
                />
            </Grid>

            <Grid item xs={12}>
                <Typography
                    variant="subtitle1"
                    color={'primary'}
                    sx={{ marginTop: 1 }}
                >
                    Boat Information
                </Typography>
                <Divider sx={{ marginBottom: 1 }} />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    label="HIN"
                    fullWidth
                    value={boatInfo.fullHin}
                    disabled
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    label="Boat Model"
                    fullWidth
                    value={boatInfo.boatModel}
                    disabled
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <TextField
                    label="Boat Manufacturer"
                    fullWidth
                    value={boatInfo.manufacturer}
                    disabled
                />
            </Grid>

            {error && (
                <Grid item xs={12}>
                    <Typography color="error" align="center">
                        {error}
                    </Typography>
                </Grid>
            )}
            <Grid item xs={12} md={8}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirm}
                    disabled={submitting}
                    sx={{
                        width: '100%',
                        height: 55,
                    }}
                >
                    {submitting ? 'Creating...' : 'Confirm Registration'}
                </Button>
            </Grid>
            <Grid item xs={12} md={4}>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleReset}
                    disabled={submitting}
                    sx={{
                        width: '100%',
                        height: 55,
                    }}
                >
                    Reset Form
                </Button>
            </Grid>
        </Grid>
    )
}

export default ConfirmRegistrationForm
